import { FaChartLine, FaTasks, FaCalendarCheck, FaVoteYea, FaUsers, FaClock, FaCheckCircle, FaExclamationTriangle, FaTrophy } from "react-icons/fa";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  // Sample data - in production, this would come from your backend
  const metrics = {
    upcomingMeetings: 3,
    openActionItems: 12,
    activeVotes: 2,
    activeTasks: 18,
    totalMembers: 30,
    averageAttendance: 85,
    completedTasksThisMonth: 24,
    overdueItems: 5,
  };

  const recentActivity = [
    {
      id: 1,
      type: "meeting",
      title: "Steering Committee Meeting scheduled",
      time: "2 hours ago",
      icon: <FaCalendarCheck />,
      color: "#667eea",
    },
    {
      id: 2,
      type: "task",
      title: "3 tasks completed by Communications team",
      time: "5 hours ago",
      icon: <FaCheckCircle />,
      color: "#48bb78",
    },
    {
      id: 3,
      type: "vote",
      title: "New vote: Q1 Budget Approval",
      time: "1 day ago",
      icon: <FaVoteYea />,
      color: "#9f7aea",
    },
    {
      id: 4,
      type: "action",
      title: "Action item overdue: Newsletter content",
      time: "1 day ago",
      icon: <FaExclamationTriangle />,
      color: "#f56565",
    },
  ];

  const committeeActivity = [
    { name: "Steering Committee", tasks: 8, completed: 6, percentage: 75 },
    { name: "Academic Staff", tasks: 12, completed: 10, percentage: 83 },
    { name: "Researchers", tasks: 6, completed: 4, percentage: 67 },
    { name: "Communications", tasks: 10, completed: 9, percentage: 90 },
    { name: "HR", tasks: 8, completed: 7, percentage: 88 },
    { name: "Belonging", tasks: 5, completed: 5, percentage: 100 },
  ];

  const urgentItems = [
    {
      id: 1,
      title: "Review budget proposals from all committees",
      type: "Action Item",
      dueDate: "Jan 20, 2026",
      assignedTo: "Josie Simm",
      priority: "high",
    },
    {
      id: 2,
      title: "Q1 Budget Approval Vote",
      type: "Vote",
      dueDate: "Jan 15, 2026",
      assignedTo: "All Members",
      priority: "high",
    },
    {
      id: 3,
      title: "Newsletter content submission",
      type: "Task",
      dueDate: "Jan 10, 2026",
      assignedTo: "Communications",
      priority: "urgent",
    },
  ];

  const MetricCard = ({ title, value, icon, color, subtitle, link }) => (
    <Link
      to={link || "#"}
      style={{
        background: "white",
        borderRadius: "12px",
        padding: "24px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
        border: "1px solid #e2e8f0",
        transition: "all 0.2s",
        textDecoration: "none",
        display: "block",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.12)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.08)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "16px" }}>
        <div>
          <div style={{ fontSize: "14px", color: "#718096", marginBottom: "8px", fontWeight: "500" }}>
            {title}
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700", color: "#2d3748" }}>
            {value}
          </div>
          {subtitle && (
            <div style={{ fontSize: "12px", color: "#a0aec0", marginTop: "4px" }}>
              {subtitle}
            </div>
          )}
        </div>
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: `${color}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: color,
            fontSize: "20px",
          }}
        >
          {icon}
        </div>
      </div>
    </Link>
  );

  return (
    <div
      style={{
        padding: "40px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#2d3748",
            marginBottom: "8px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <FaChartLine style={{ color: "#667eea" }} />
          Dashboard
        </h1>
        <p style={{ color: "#718096", fontSize: "16px" }}>
          Overview of Staff Council activity and metrics
        </p>
      </div>

      {/* Metrics Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <MetricCard
          title="Upcoming Meetings"
          value={metrics.upcomingMeetings}
          icon={<FaCalendarCheck />}
          color="#667eea"
          subtitle="Next 30 days"
          link="/meetings"
        />
        <MetricCard
          title="Open Action Items"
          value={metrics.openActionItems}
          icon={<FaTasks />}
          color="#ed8936"
          subtitle={`${metrics.overdueItems} overdue`}
          link="/action-items"
        />
        <MetricCard
          title="Active Votes"
          value={metrics.activeVotes}
          icon={<FaVoteYea />}
          color="#9f7aea"
          subtitle="Awaiting responses"
          link="/votes"
        />
        <MetricCard
          title="Active Tasks"
          value={metrics.activeTasks}
          icon={<FaCheckCircle />}
          color="#48bb78"
          subtitle={`${metrics.completedTasksThisMonth} completed this month`}
          link="/tasks"
        />
      </div>

      {/* Two Column Layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "30px" }}>
        {/* Recent Activity */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", marginBottom: "20px" }}>
            Recent Activity
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {recentActivity.map((activity) => (
              <div
                key={activity.id}
                style={{
                  display: "flex",
                  gap: "12px",
                  padding: "12px",
                  background: "#f7fafc",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    background: `${activity.color}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: activity.color,
                    flexShrink: 0,
                  }}
                >
                  {activity.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "14px", color: "#2d3748", fontWeight: "500", marginBottom: "4px" }}>
                    {activity.title}
                  </div>
                  <div style={{ fontSize: "12px", color: "#a0aec0" }}>
                    {activity.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Committee Performance */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", marginBottom: "20px" }}>
            Committee Activity
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {committeeActivity.map((committee, index) => (
              <div key={index}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ fontSize: "13px", color: "#2d3748", fontWeight: "500" }}>
                    {committee.name}
                  </span>
                  <span style={{ fontSize: "13px", color: "#718096" }}>
                    {committee.completed}/{committee.tasks} tasks
                  </span>
                </div>
                <div style={{ width: "100%", height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                  <div
                    style={{
                      width: `${committee.percentage}%`,
                      height: "100%",
                      background: committee.percentage === 100 ? "#48bb78" : committee.percentage >= 75 ? "#667eea" : "#ed8936",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Urgent Items */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "24px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
          <FaExclamationTriangle style={{ color: "#ed8936" }} />
          Items Needing Attention
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {urgentItems.map((item) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                background: item.priority === "urgent" ? "#fff5f5" : "#fffaf0",
                border: `1px solid ${item.priority === "urgent" ? "#feb2b2" : "#fbd38d"}`,
                borderRadius: "8px",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "15px", color: "#2d3748", fontWeight: "600", marginBottom: "4px" }}>
                  {item.title}
                </div>
                <div style={{ fontSize: "13px", color: "#718096" }}>
                  {item.type} • Assigned to: {item.assignedTo}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "12px", color: "#718096", marginBottom: "2px" }}>
                    Due Date
                  </div>
                  <div style={{ fontSize: "14px", color: item.priority === "urgent" ? "#f56565" : "#ed8936", fontWeight: "600" }}>
                    {item.dueDate}
                  </div>
                </div>
                <span
                  style={{
                    padding: "4px 12px",
                    background: item.priority === "urgent" ? "#f56565" : "#ed8936",
                    color: "white",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  {item.priority}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginTop: "30px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "14px", color: "#718096", marginBottom: "8px" }}>
            Total Members
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#667eea" }}>
            {metrics.totalMembers}
          </div>
        </div>
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "14px", color: "#718096", marginBottom: "8px" }}>
            Avg Attendance
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#48bb78" }}>
            {metrics.averageAttendance}%
          </div>
        </div>
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "14px", color: "#718096", marginBottom: "8px" }}>
            Tasks This Month
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#9f7aea" }}>
            {metrics.completedTasksThisMonth}
          </div>
        </div>
      </div>
    </div>
  );
}

